// THE FRAME'S SIDE OF THE PHONE VIEW (Session BDZ). Runs inside the stage's iframe only; anywhere else it does
// nothing at all. It tells the stage where the app is, takes the stage's text size and navigation requests, follows
// the app theme when the stage's control changes it, and shows the "Preview only" notice when a write is refused.
//
// It talks to the stage and nobody else: a message is acted on only when it comes from this window's PARENT and from
// this page's own origin, and the only thing it ever sends is a path on this origin.
import { setTheme, THEME_STORAGE_KEY } from "./theme";
import {
  PHONE_VIEW_BLOCKED_EVENT,
  PHONE_VIEW_BLOCKED_MESSAGE,
  PHONE_VIEW_MSG,
  PHONE_TEXT_SCALES,
  isInPhonePreviewFrame,
  noteGesture,
  normalisePhonePath,
} from "./phonePreview";

/** What the bridge needs of the router: its state, its navigation and its subscription. */
export interface FrameRouter {
  state: { location: { pathname: string; search: string } };
  navigate: (to: string) => unknown;
  subscribe: (listener: (state: { location: { pathname: string; search: string } }) => void) => () => void;
}

const NOTICE_ID = "ele-phone-view-notice";
const NOTICE_MS = 3500;

/** Apply the system text size the way a handset's larger text setting does for rem text: the root font size. */
export function applyTextScale(percent: number, doc: Document = document): void {
  const ok = (PHONE_TEXT_SCALES as readonly number[]).includes(percent);
  doc.documentElement.style.fontSize = ok && percent !== 100 ? `${percent}%` : "";
}

function showNotice(doc: Document, win: Window): void {
  doc.getElementById(NOTICE_ID)?.remove();
  const el = doc.createElement("div");
  el.id = NOTICE_ID;
  el.setAttribute("role", "status");
  el.textContent = PHONE_VIEW_BLOCKED_MESSAGE;
  const s = el.style;
  s.position = "fixed";
  s.left = "16px";
  s.right = "16px";
  s.top = "50%";
  s.transform = "translateY(-50%)";
  s.zIndex = "2147483000";
  s.padding = "14px 16px";
  s.borderRadius = "12px";
  s.border = "1px solid var(--ele-hairline-strong)";
  s.background = "var(--ele-card)";
  s.color = "var(--ele-text-strong)";
  s.fontSize = "14px";
  s.textAlign = "center";
  s.pointerEvents = "none";
  // Inside the shell's own scope when there is one, so the light theme's tokens apply to it (they bind only there).
  (doc.querySelector(".office-ui, .engineer-ui") ?? doc.body).appendChild(el);
  win.setTimeout(() => el.remove(), NOTICE_MS);
}

/** Install the bridge. Returns a function that removes it (for tests); outside the frame it is a no-op. */
export function installPhonePreviewFrame(router: FrameRouter, win: Window = window): () => void {
  if (!isInPhonePreviewFrame(win)) return () => undefined;
  const doc = win.document;
  doc.documentElement.dataset.phoneView = "true";
  const origin = win.location.origin;
  const post = (message: Record<string, unknown>) => win.parent.postMessage(message, origin);
  const reportPath = (loc: { pathname: string; search: string }) => post({ type: PHONE_VIEW_MSG.path, path: `${loc.pathname}${loc.search}` });

  const unsubscribe = router.subscribe((state) => reportPath(state.location));

  const onMessage = (event: MessageEvent) => {
    // Only the stage that framed this page, on this origin.
    if (event.origin !== origin || event.source !== win.parent) return;
    const data = event.data as { type?: unknown; percent?: unknown; path?: unknown } | null;
    if (!data || typeof data !== "object") return;
    if (data.type === PHONE_VIEW_MSG.textScale && typeof data.percent === "number") applyTextScale(data.percent, doc);
    else if (data.type === PHONE_VIEW_MSG.navigate && typeof data.path === "string") {
      const path = normalisePhonePath(data.path, origin);
      if (path) void router.navigate(path);
    }
  };
  win.addEventListener("message", onMessage);

  // The stage's theme control writes the one shared preference; this page hears it as a storage event and follows.
  const onStorage = (event: StorageEvent) => {
    if (event.key === THEME_STORAGE_KEY && (event.newValue === "light" || event.newValue === "dark")) setTheme(event.newValue);
  };
  win.addEventListener("storage", onStorage);

  // A refusal becomes a visible notice only just after a person did something (see phonePreview.noteGesture).
  const gesture = () => noteGesture();
  for (const type of ["pointerdown", "keydown", "click"]) win.addEventListener(type, gesture, true);
  const onBlocked = () => showNotice(doc, win);
  win.addEventListener(PHONE_VIEW_BLOCKED_EVENT, onBlocked);

  reportPath(router.state.location);
  post({ type: PHONE_VIEW_MSG.ready });

  return () => {
    unsubscribe();
    win.removeEventListener("message", onMessage);
    win.removeEventListener("storage", onStorage);
    for (const type of ["pointerdown", "keydown", "click"]) win.removeEventListener(type, gesture, true);
    win.removeEventListener(PHONE_VIEW_BLOCKED_EVENT, onBlocked);
    doc.getElementById(NOTICE_ID)?.remove();
    delete doc.documentElement.dataset.phoneView;
    doc.documentElement.style.fontSize = "";
  };
}
